import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpHeaders, HttpInterceptor, HttpHandler, HttpRequest, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AppConfig } from './app.config';
import { GlobalService } from './global.service';
import { MenuController, NavController } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { IonicService } from './ionic.service';

@Injectable()
export class AjaxService implements HttpInterceptor {

  jwtHelper: JwtHelperService = new JwtHelperService();
  private static loggingOut = false;

  constructor(
    private http: HttpClient,
    private menu: MenuController,
    private storage: Storage,
    private navCtrl: NavController,
    private injector: Injector
  ) { }

  private applyAuthHeaders(headers: HttpHeaders): HttpHeaders {
    const user = GlobalService.user;
    if (user && user.access_token) {
      headers = headers.set("Authorization", "Bearer " + user.access_token);
    }
    if (user && user.session_proof) {
      headers = headers.set("X-Session-Proof", user.session_proof);
    }
    return headers;
  }

  private sessionTerminatedLogout(error: HttpErrorResponse): boolean {
    return error.status === 403 && error.headers.get('X-Auth-Session-Terminated') === '1';
  }

  private shouldForceLogout(error: HttpErrorResponse, request: HttpRequest<any>): boolean {
    if (AjaxService.loggingOut) {
      return false;
    }
    const url = (request.url || '').toLowerCase();
    if (url.includes('/user/logout') || url.includes('/token')) {
      return false;
    }
    return error.status === 401
      || error.status === 419
      || this.sessionTerminatedLogout(error);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let headers = new HttpHeaders();
    
    // Add authorization header if required
    if (!request.headers.has('skip-auth') && !environment["skipAuth"]) {
      if (GlobalService.user) {
        headers = this.applyAuthHeaders(headers);
      }
    }

    // Clone the request with updated headers
    const modifiedRequest = request.clone({ headers });

    // Continue with the modified request
    return next.handle(modifiedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.shouldForceLogout(error, request)) {
          this.logout();
        }
        return throwError(error);
      })
    );
  }

  callService1(serviceUrl: string, skipAuth: boolean = false, method = 'Get', data = null): Observable<any> {
    let headers;

    if (!skipAuth && !environment["skipAuth"]) {
      headers = new HttpHeaders({
        'Content-Type': 'application/json',
      });

      if (GlobalService.user)
        headers = this.applyAuthHeaders(headers);
      headers = { headers: headers };
    }

    let requestObservable: Observable<any>;

    switch (method) {
      case 'Get':
        requestObservable = this.http.get(AppConfig.baseUrl + serviceUrl, headers);
        break;
      case 'Delete':
        requestObservable = this.http.delete(AppConfig.baseUrl + serviceUrl, headers);
        break;
      case 'Put':
        requestObservable = this.http.put(AppConfig.baseUrl + serviceUrl, data, headers);
        break;
      case 'Post':
        requestObservable = this.http.post(AppConfig.baseUrl + serviceUrl, data, headers);
        break;
      default:
        throw new Error(`Invalid HTTP method: ${method}`);
    }
    return requestObservable.pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.shouldForceLogout(error, { url: AppConfig.baseUrl + serviceUrl } as HttpRequest<any>)) {
          this.logout();
        }
        return throwError(error);
      })
    );
  }

  uploadService(serviceUrl: string, skipAuth: boolean = false, method = 'Get', data = null): Observable<any> {
    let headers;

    if (!skipAuth && !environment["skipAuth"]) {
      headers = new HttpHeaders();

      if (GlobalService.user)
        headers = this.applyAuthHeaders(headers);
      headers = { headers: headers };
    }

    let requestObservable: Observable<any>;

    switch (method) {
      case 'Get':
        requestObservable = this.http.get(AppConfig.baseUrl + serviceUrl, headers);
        break;
      case 'Delete':
        requestObservable = this.http.delete(AppConfig.baseUrl + serviceUrl, headers);
        break;
      case 'Put':
        requestObservable = this.http.put(AppConfig.baseUrl + serviceUrl, data, headers);
        break;
      case 'Post':
        requestObservable = this.http.post(AppConfig.baseUrl + serviceUrl, data, headers);
        break;
      default:
        throw new Error(`Invalid HTTP method: ${method}`);
    }
    return requestObservable.pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.shouldForceLogout(error, { url: AppConfig.baseUrl + serviceUrl } as HttpRequest<any>)) {
          this.logout();
        }
        return throwError(error);
      })
    );
  }

  callService(serviceUrl: string, skipAuth: boolean = false, method = 'Get', data = null): Observable<any> {
    return this.callService1(serviceUrl, skipAuth, method, data);
  }

  callGetService(serviceUrl: string, skipAuth: boolean = false): Observable<any> {
    return this.callService(serviceUrl, skipAuth, 'Get');
  }

  callPostService(serviceUrl: string, data: any, skipAuth: boolean = false): Observable<any> {
    return this.callService(serviceUrl, skipAuth, 'Post', data);
  }

  callDeleteService(serviceUrl: string): Observable<any> {
    return this.callService(serviceUrl, false, 'Delete');
  }

  save(serviceUrl: string, data: any): Observable<any> {
    return this.callPostService(serviceUrl + '/save', data);
  }

  public logout() {
    if (AjaxService.loggingOut) {
      return;
    }
    AjaxService.loggingOut = true;
    void this.injector.get(IonicService).clearLoaders();
    this.menu.close();
    this.menu.enable(false);
    GlobalService.user = null;
    this.storage.remove('user');
    this.navCtrl.navigateRoot('/login').finally(() => {
      AjaxService.loggingOut = false;
    });
  }
}
